import { Button, IconButton, FormControl, Select, MenuItem, Typography } from "@mui/material"
import { useFormikContext } from "formik"
import { Plus, Trash2, Pencil, Users } from "lucide-mui"
import React, { useState, useEffect, useRef, useMemo } from "react"

import SvgCanvas from "@/components/settings/venue_plan/plan_editor/svg_canvas"
import { useSvgOverlays } from "@/components/settings/venue_plan/plan_editor/use_svg_overlays"
import { useSvgSelection } from "@/components/settings/venue_plan/plan_editor/use_svg_selection"
import { useZoomPan } from "@/components/settings/venue_plan/plan_editor/use_zoom_pan"
import ZoomControls from "@/components/settings/venue_plan/plan_editor/zoom_controls"
import VenuePlanUploader from "@/components/settings/venue_plan/venue_plan_uploader"
import i18n from "@/i18n"

import type { ConfirmDialogState, FormValues } from "./types"

const STYLES = {
  root: "flex-1 flex flex-col min-h-0",
  toolbar: "flex-shrink-0 flex items-center justify-between gap-2 mb-4 bg-white border border-gray-200 rounded-lg p-3",
  toolbarActions: "flex items-center gap-1",
  iconButton: "border border-solid border-gray-300 rounded",
  iconSmall: "w-5 h-5",
  canvasArea: "flex-1 border border-gray-200 rounded-lg overflow-hidden bg-gray-50 relative min-h-[400px] max-h-full",
  emptyState: "flex items-center justify-center h-full text-gray-400",
  bottomPanel: "flex-shrink-0 bg-white border border-gray-200 rounded-lg p-4 mt-4",
  selectedElementRow: "flex items-center gap-2 mb-3",
  selectedElementLabel: "text-blue-600 text-sm font-medium",
  selectedElementValue: "text-sm text-gray-600",
  multiSelectHint: "text-xs text-gray-400 ml-auto",
  assignRow: "flex items-center gap-4",
  capacitySelect: "min-w-[120px]",
  capacityIcon: "w-4 h-4 mr-2 text-gray-500",
  capacitySeparator: "text-gray-400",
  capacityHint: "text-gray-500 mt-2 block",
  actionsRow: "flex items-center justify-end gap-3 mt-4",
} as const

const MAX_TABLE_CAPACITY = 20
const CAPACITY_OPTIONS = Array.from({ length: MAX_TABLE_CAPACITY }, (_, i) => i + 1)

type FloorPlanCanvasProps = {
  dirty: boolean
  saving: boolean
  resetForm: () => void
  confirmDialog: ConfirmDialogState
  setConfirmDialog: React.Dispatch<React.SetStateAction<ConfirmDialogState>>
  onError: (error: string) => void
}

const FloorPlanCanvas: React.FC<FloorPlanCanvasProps> = ({
  dirty,
  saving,
  resetForm,
  confirmDialog: _confirmDialog,
  setConfirmDialog,
  onError,
}) => {
  const { values, setFieldValue } = useFormikContext<FormValues>()
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(0)
  const [selectedMinCapacity, setSelectedMinCapacity] = useState<number | "">("")
  const [selectedMaxCapacity, setSelectedMaxCapacity] = useState<number | "">("")
  const [showAssignedTables] = useState(true)
  const containerRef = useRef<HTMLDivElement>(null)
  const svgWrapperRef = useRef<HTMLDivElement>(null)

  const allTables = useMemo(
    () => values.tableGroups.flatMap((group) => group.tables).filter((t) => !t.deleted),
    [values.tableGroups],
  )
  const currentPlan = values.plans[selectedPlanIndex]

  const handlePlanChange = (plan: string | null) => {
    if (currentPlan) {
      const newPlans = [...values.plans]
      newPlans[selectedPlanIndex] = { ...newPlans[selectedPlanIndex], plan }
      void setFieldValue("plans", newPlans)
    }
  }

  const { selectedElements, selectedTableId, handleTableAssignment, findElementById } = useSvgSelection({
    containerRef,
    svgContent: currentPlan?.plan || null,
    onPlanChange: handlePlanChange,
  })

  const {
    zoom,
    pan,
    isDragging,
    handleZoomIn,
    handleZoomOut,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleWheel,
  } = useZoomPan({ svgWrapperRef, containerRef })

  useSvgOverlays({
    containerRef,
    selectedElements,
    showAssignedTables,
    svgContent: currentPlan?.plan || null,
    tables: allTables,
    selectedTableId,
    findElementById,
  })

  useEffect(() => {
    if (selectedTableId) {
      const table = allTables.find((t) => t.id === selectedTableId)
      if (table) {
        setSelectedMinCapacity(table.minCapacity ?? "")
        setSelectedMaxCapacity(table.maxCapacity ?? "")
      }
    } else {
      setSelectedMinCapacity("")
      setSelectedMaxCapacity("")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when selected table changes
  }, [selectedTableId])

  const handleTableAssign = (tableId: string | null) => {
    handleTableAssignment(tableId)

    if (tableId) {
      const table = allTables.find((t) => t.id === tableId)
      if (table) {
        setSelectedMinCapacity(table.minCapacity ?? "")
        setSelectedMaxCapacity(table.maxCapacity ?? "")
      }
    } else {
      setSelectedMinCapacity("")
      setSelectedMaxCapacity("")
    }
  }

  const updateCapacityInFormik = (field: "minCapacity" | "maxCapacity", value: number | "") => {
    if (!selectedTableId) return

    const groupIndex = values.tableGroups.findIndex((g) => g.tables.some((t) => t.id === selectedTableId))
    if (groupIndex === -1) return

    const tableIndex = values.tableGroups[groupIndex].tables.findIndex((t) => t.id === selectedTableId)
    if (tableIndex === -1) return

    void setFieldValue(`tableGroups[${groupIndex}].tables[${tableIndex}].${field}`, value === "" ? null : value)
  }

  const handleMinCapacityChange = (value: number | "") => {
    setSelectedMinCapacity(value)
    updateCapacityInFormik("minCapacity", value)
  }

  const handleMaxCapacityChange = (value: number | "") => {
    setSelectedMaxCapacity(value)
    updateCapacityInFormik("maxCapacity", value)
  }

  const handleAddPlan = () => {
    const newPlan = {
      sectionName: i18n.t("app.settings.venues.venue_layout.plan.new_plan_section"),
      plan: null,
      position: values.plans.length,
      default: values.plans.length === 0,
      isNew: true,
    }
    void setFieldValue("plans", [...values.plans, newPlan])
    setSelectedPlanIndex(values.plans.length)
  }

  const handleRemovePlan = (index: number) => {
    const wasDefault = values.plans[index].default
    const reindexedPlans = values.plans.filter((_, i) => i !== index).map((m, i) => ({ ...m, position: i }))

    if (wasDefault && reindexedPlans.length > 0) {
      reindexedPlans[0].default = true
    }

    void setFieldValue("plans", reindexedPlans)
    if (selectedPlanIndex >= reindexedPlans.length) {
      setSelectedPlanIndex(Math.max(0, reindexedPlans.length - 1))
    }
  }

  const handlePlanUpload = (svgContent: string) => {
    if (currentPlan) {
      const newPlans = [...values.plans]
      newPlans[selectedPlanIndex] = { ...newPlans[selectedPlanIndex], plan: svgContent }
      void setFieldValue("plans", newPlans)
    }
  }

  const selectedElementInfo =
    selectedElements.length > 0
      ? selectedElements.map((sel) => `${sel.element.tagName}${sel.element.id ? ` #${sel.element.id}` : ""}`).join(", ")
      : null

  return (
    <div className={STYLES.root}>
      <div className={STYLES.toolbar}>
        <FormControl size="small" sx={{ minWidth: 200, maxWidth: 300, flex: 1 }}>
          <Select
            value={selectedPlanIndex}
            onChange={(e) => setSelectedPlanIndex(Number(e.target.value))}
            displayEmpty
            data-testid="settings-venue-layout-plan-selector"
          >
            {values.plans.map((plan, index) => (
              <MenuItem key={index} value={index}>
                {plan.sectionName || `Section ${index + 1}`}
                {plan.default ? " ★" : null}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <div className={STYLES.toolbarActions}>
          <IconButton
            size="small"
            onClick={handleAddPlan}
            className={STYLES.iconButton}
            data-testid="settings-venue-layout-btn-add-plan"
          >
            <Plus className={STYLES.iconSmall} />
          </IconButton>
          <IconButton
            size="small"
            disabled={!currentPlan}
            className={STYLES.iconButton}
            data-testid="settings-venue-layout-btn-edit-plan"
          >
            <Pencil className={STYLES.iconSmall} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleRemovePlan(selectedPlanIndex)}
            disabled={values.plans.length <= 1}
            className={STYLES.iconButton}
            data-testid="settings-venue-layout-btn-remove-plan"
          >
            <Trash2 className={STYLES.iconSmall} />
          </IconButton>
          <VenuePlanUploader
            hasExistingPlan={!!currentPlan?.plan}
            onFileSelected={handlePlanUpload}
            onError={onError}
            iconButton
            className={STYLES.iconButton}
          />
        </div>
      </div>

      <div className={STYLES.canvasArea}>
        {currentPlan?.plan ? (
          <>
            <SvgCanvas
              containerRef={containerRef}
              svgWrapperRef={svgWrapperRef}
              zoom={zoom}
              pan={pan}
              isDragging={isDragging}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onWheel={handleWheel}
            />
            <ZoomControls zoom={zoom} onZoomIn={handleZoomIn} onZoomOut={handleZoomOut} />
          </>
        ) : (
          <div className={STYLES.emptyState}>{i18n.t("app.settings.venues.venue_layout.plan.no_plan")}</div>
        )}
      </div>

      <div className={STYLES.bottomPanel}>
        <div className={STYLES.selectedElementRow}>
          <span className={STYLES.selectedElementLabel}>
            {i18n.t("app.settings.venues.venue_layout.plan.selected_element")}
          </span>
          <span className={STYLES.selectedElementValue}>
            {selectedElementInfo || i18n.t("app.settings.venues.venue_layout.plan.no_element_selected")}
          </span>
          <span className={STYLES.multiSelectHint}>
            ({i18n.t("app.settings.venues.venue_layout.plan.multi_select_hint")})
          </span>
        </div>

        <div className={STYLES.assignRow}>
          <FormControl size="small" sx={{ minWidth: 180, maxWidth: 300, flex: 1 }}>
            <Select
              value={selectedTableId || ""}
              onChange={(e) => handleTableAssign(e.target.value || null)}
              disabled={selectedElements.length === 0}
              displayEmpty
              startAdornment={<Users className={STYLES.capacityIcon} />}
              data-testid="settings-venue-layout-table-assign-select"
            >
              <MenuItem value="">{i18n.t("app.settings.venues.venue_layout.plan.assign_table")}</MenuItem>
              {allTables.map((table) => (
                <MenuItem key={table.id || table.name} value={table.id || ""} disabled={!table.id}>
                  {table.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" className={STYLES.capacitySelect}>
            <Select<number | "">
              value={selectedMinCapacity}
              onChange={(e) => {
                const value = e.target.value
                handleMinCapacityChange(value === "" ? "" : Number(value))
              }}
              disabled={!selectedTableId}
              displayEmpty
              startAdornment={<Users className={STYLES.capacityIcon} />}
              data-testid="settings-venue-layout-min-capacity-select"
            >
              <MenuItem value="">{i18n.t("app.settings.venues.venue_layout.minimum")}</MenuItem>
              {CAPACITY_OPTIONS.map((num) => (
                <MenuItem key={num} value={num}>
                  {num}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <span className={STYLES.capacitySeparator}>—</span>

          <FormControl size="small" className={STYLES.capacitySelect}>
            <Select<number | "">
              value={selectedMaxCapacity}
              onChange={(e) => {
                const value = e.target.value
                handleMaxCapacityChange(value === "" ? "" : Number(value))
              }}
              disabled={!selectedTableId}
              displayEmpty
              startAdornment={<Users className={STYLES.capacityIcon} />}
              data-testid="settings-venue-layout-max-capacity-select"
            >
              <MenuItem value="">{i18n.t("app.settings.venues.venue_layout.maximum")}</MenuItem>
              {CAPACITY_OPTIONS.map((num) => (
                <MenuItem key={num} value={num}>
                  {num}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        <Typography variant="caption" className={STYLES.capacityHint}>
          {i18n.t("app.settings.venues.venue_layout.capacity_hint")}
        </Typography>
      </div>

      <div className={STYLES.actionsRow}>
        <Button
          variant="outlined"
          data-testid="settings-venue-layout-btn-cancel"
          onClick={() => {
            if (dirty) {
              setConfirmDialog({
                open: true,
                title: i18n.t("app.settings.venues.venue_layout.cancel"),
                message: i18n.t("app.settings.venues.venue_layout.confirm_cancel"),
                onConfirm: () => {
                  resetForm()
                  setConfirmDialog((prev) => ({ ...prev, open: false }))
                },
              })
            } else {
              resetForm()
            }
          }}
        >
          {i18n.t("app.settings.venues.venue_layout.cancel")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          type="submit"
          disabled={saving || !dirty}
          data-testid="settings-venue-layout-btn-save"
        >
          {i18n.t("app.settings.venues.venue_layout.save_changes")}
        </Button>
      </div>
    </div>
  )
}

export default FloorPlanCanvas
